/**
 * TrustGrid Cases Page
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Button, Spin, Select, Modal, Form, Input, DatePicker } from 'antd';
import { PlusOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  listCases,
  getCase,
  createCase,
  updateCase,
  deleteCase
} from '../api/trustgridApi';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import ProjectSelector from './ProjectSelector';
import { showError, showSuccess } from '../../../utils/notifications';
import { handleCrossOrgError } from '../utils/errorHandlers';

const { Option } = Select;
const { TextArea } = Input;

function Cases() {
  const navigate = useNavigate();
  const { selectedProjectId, selectProject } = useTrustGrid();
  const projectId = selectedProjectId || 3;
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCases();
  }, [statusFilter]);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const status = statusFilter === 'all' ? null : statusFilter;
      const res = await listCases(projectId, status);
      setCases(res.data.cases || []);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Cases');
      if (!wasHandled) {
        console.error('Failed to fetch cases:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async (values) => {
    try {
      await createCase(projectId, {
        ...values,
        due_date: values.due_date ? values.due_date.toISOString() : null
      });
      showSuccess('Case created');
      setCreateModalVisible(false);
      form.resetFields();
      fetchCases();
    } catch (error) {
      console.error('Failed to create case:', error);
      showError(error);
    }
  };

  const handleUpdateStatus = async (caseId, newStatus) => {
    try {
      await updateCase(caseId, { status: newStatus });
      showSuccess('Case updated');
      fetchCases();
    } catch (error) {
      console.error('Failed to update case:', error);
      showError(error);
    }
  };

  const handleDelete = async (caseId) => {
    Modal.confirm({
      title: 'Delete Case?',
      content: 'This will unlink all findings from this case. The action cannot be undone.',
      onOk: async () => {
        try {
          await deleteCase(caseId);
          showSuccess('Case deleted');
          fetchCases();
        } catch (error) {
          console.error('Failed to delete case:', error);
          showError(error);
        }
      }
    });
  };

  const handleViewDetail = async (caseItem) => {
    try {
      const res = await getCase(caseItem.id);
      setSelectedCase(res.data);
      setDetailModalVisible(true);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Cases');
      if (!wasHandled) {
        console.error('Failed to fetch case details:', error);
        showError(error);
      }
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'gold';
      case 'low': return 'blue';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'red';
      case 'in_progress': return 'blue';
      case 'resolved': return 'green';
      case 'closed': return 'gray';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text, record) => (
        <a onClick={() => handleViewDetail(record)}>{text}</a>
      )
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>{priority}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => (
        <Select
          value={status}
          size="small"
          style={{ width: '100%' }}
          onChange={(value) => handleUpdateStatus(record.id, value)}
        >
          <Option value="open">Open</Option>
          <Option value="in_progress">In Progress</Option>
          <Option value="resolved">Resolved</Option>
          <Option value="closed">Closed</Option>
        </Select>
      )
    },
    {
      title: 'Findings',
      dataIndex: 'findings_count',
      key: 'findings_count',
      width: 100,
      render: (count) => `${count} finding${count !== 1 ? 's' : ''}`
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 120,
      render: (date) => date ? new Date(date).toLocaleDateString() : '-'
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            title="View Details"
          />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            title="Delete"
          />
        </div>
      )
    }
  ];

  return (
    <div>
      <ProjectSelector />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0 }}>Cases</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
          >
            <Option value="all">All Status</Option>
            <Option value="open">Open</Option>
            <Option value="in_progress">In Progress</Option>
            <Option value="resolved">Resolved</Option>
            <Option value="closed">Closed</Option>
          </Select>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
            className="custom-button"
          >
            New Case
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : cases.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          <p>No cases found</p>
          <p style={{ fontSize: '14px' }}>
            Cases are used to track remediation efforts for multiple findings.
          </p>
        </div>
      ) : (
        <Table
          dataSource={cases}
          columns={columns}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true
          }}
          bordered
        />
      )}

      {/* Create Case Modal */}
      <Modal
        title="Create Case"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateCase}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Case title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={4} placeholder="Describe the case" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue="medium">
            <Select>
              <Option value="critical">Critical</Option>
              <Option value="high">High</Option>
              <Option value="medium">Medium</Option>
              <Option value="low">Low</Option>
            </Select>
          </Form.Item>
          <Form.Item name="due_date" label="Due Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" className="custom-button">
              Create Case
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={`Case #${selectedCase?.id}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {selectedCase && (
          <div>
            <p><strong>Title:</strong> {selectedCase.title}</p>
            <p><strong>Description:</strong> {selectedCase.description || '-'}</p>
            <p>
              <strong>Priority:</strong>{' '}
              <Tag color={getPriorityColor(selectedCase.priority)}>
                {selectedCase.priority}
              </Tag>
            </p>
            <p>
              <strong>Status:</strong>{' '}
              <Tag color={getStatusColor(selectedCase.status)}>
                {selectedCase.status}
              </Tag>
            </p>
            <p><strong>Created:</strong> {new Date(selectedCase.created_at).toLocaleString()}</p>
            {selectedCase.due_date && (
              <p><strong>Due Date:</strong> {new Date(selectedCase.due_date).toLocaleDateString()}</p>
            )}
            {selectedCase.resolved_at && (
              <p><strong>Resolved:</strong> {new Date(selectedCase.resolved_at).toLocaleString()}</p>
            )}
            {selectedCase.resolution_notes && (
              <p><strong>Resolution Notes:</strong> {selectedCase.resolution_notes}</p>
            )}

            <h4 style={{ marginTop: '20px' }}>Findings ({selectedCase.findings?.length || 0})</h4>
            {selectedCase.findings && selectedCase.findings.length > 0 ? (
              <ul>
                {selectedCase.findings.map((finding) => (
                  <li key={finding.id}>
                    <strong>#{finding.id}</strong>: {finding.title}{' '}
                    <Tag color={getPriorityColor(finding.severity)} size="small">
                      {finding.severity}
                    </Tag>{' '}
                    <Tag color={getStatusColor(finding.status)} size="small">
                      {finding.status}
                    </Tag>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#888' }}>No findings linked to this case</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Cases;
